/**
 * @file contains all the types related to DMS
 */
import { FinanceResponseData } from './financingTypes';

export enum DMSType {
  CDK = 'cdk',
  DT = 'dealertrack',
  REYNOLDS = 'reynolds',
  STANDALONE = 'standalone',
  TEKION = 'tekion',
}

export const DmsTypeName = {
  [DMSType.CDK]: 'CDK',
  [DMSType.DT]: 'Dealertrack',
  [DMSType.REYNOLDS]: 'Reynolds & Reynolds',
  [DMSType.STANDALONE]: 'Other',
  [DMSType.TEKION]: 'Tekion',
};

/*
 What the DMS Code Represents
 Used in conjunction with dmsCode field.
*/
export enum DMSCodeType {
  WE_OWE = 'WeOwe',
  PRODUCT = 'Product',
  FEE = 'Fee',
}

/**
 * Representation of any DMSCustomer. At the time of writing,
 * this represent a customer in CDK DMS, but in future,
 * for a different DMS there might be additional fields, they could be added here
 * Alternatively, this can be generic interface taking a type and customer types for
 * different DMS could have their own type. For example,
 *
 * export interface DMSCustomer<T> {
 *     data: T;
 *   }
 *
 * // one type for data field type
 * export interface CDKCustomer {
 *     id: string;
 *     firstName?: string;
 *     lastName?: string;
 *     fullName?: string;
 *     phone?: string;
 *   }
 *
 * export interface DealerSocketCustomer {
 *     own set of fields for DealerSocket....
 *   }
 *
 *  The advantage of the Generics approach is that there will be no confusion
 *  about which customer fields belong to which DMS system, instead of having one
 *  DMSCustomer interface with fields merged from different DMS systems' Customer
 *
 * @interface DMSCustomer
 */
export interface DMSCustomer {
  id?: string; // CDK

  address1?: string; // DT:A45
  birthDate?: string; // DT:N8,0
  businessPhone?: string; // DT:N10,0
  businessPhoneExt?: string; // DT:N4,0
  ccid?: string; // DT:A9 - undocumented possibly deprecated
  cellPhone?: string; // DT:N10,0
  city?: string; // DT:A20
  commonConsumerId?: string; // DT:A9 - this is wrong. it's a uuid
  companyNumber?: string; // DT:A3
  county?: string; // DT:A18
  customerNumber?: string; // DT:A9
  dataToken?: string; // DT:A25
  driversLicense?: string; // DT:A15
  entityType?: string; // DT:A1 C=Company I=Individual
  email1?: string; // DT:A40
  email2?: string; // DT:A40
  firstName?: string; // DT:A15 | CDK
  fullName?: string; // CDK
  homePhone?: number; // DT:N10,0
  lastName?: string; // DT:A25 | CDK
  middleName?: string; // DT:A15
  phone?: string; // CDK
  salutation?: string; // DT:A10
  state?: string; // DT:A2 - used for search
  typeCode?: string; // DT:A1
  vin?: string; // DT:A17
  zipCode?: string; // DT:N9,0
}

export interface DMSTaxGroupDTO {
  dealerId: string;
  state: string;
  groups: DMSTaxGroupSummary[];
}

export interface DMSTaxGroupSummary {
  id: string;
  description: string;
}

export declare type CDKCustomerFields =
  | 'id'
  | 'firstName'
  | 'lastName'
  | 'fullName'
  | 'phone';

/**
 * @see https://otwiki.dms.dealertrack.com/opentrack/index.php/CustomerSearch_Request#Results_Definitions
 */
export declare type DealerTrackCustomerFields =
  | 'companyNumber'
  | 'customerNumber'
  | 'commonConsumerId'
  | 'ccid'
  | 'entityType'
  | 'lastName'
  | 'firstName'
  | 'middleName'
  | 'salutation'
  | 'address1'
  | 'city'
  | 'state'
  | 'zipCode'
  | 'county'
  | 'homePhone'
  | 'cellPhone'
  | 'businessPhone'
  | 'businessPhoneExt'
  | 'email1'
  | 'email2'
  | 'vin'
  | 'dataToken';

/**
 * CDK Only customer fields. Can be used to narrow DMSCustomer to more specific type.
 */
export type DMSCustomerCDK = Pick<DMSCustomer, CDKCustomerFields>;

/**
 * DealerTrack Only customer fields. Can be used to narrow DMSCustomer to more specific type.
 */
export type DMSCustomerDT = Pick<DMSCustomer, DealerTrackCustomerFields>;

/**
 * Representation of any DMSDeal. Currently we are interested in the
 * DealId and the checksum.
 *
 * Checksum is still specific to CDK, Once the deal has been saved in DMS software
 * by manager, the checksum changes, we will use this to see if a deal has been
 * updated by the manager or not.
 *
 * @interface DMSDeal
 */
export interface DMSDeal {
  id: string;
  checksum?: string;
  data?: FinanceResponseData;
}

export interface DMSDealInfo {
  // these fields will be set from the financing screen
  id: string;
  checksum?: string;
  selectedFinanceResponseId?: string;
  pushedAt: Date | string;

  // Below fields are added as part of DMS Reuse feature
  // https://upstartnetwork.atlassian.net/browse/REL-22196
  // We need to track the dmsDealId pushed from the desking screen
  dmsDealIdFromDesking?: string;
  dmsDealChecksumFromDesking?: string;
  deskingOfferPushedAt?: Date | string;
}

export type DTEntityId = string;

export enum TekionEntityType {
  FNI = 'fni',
  ACCESSORY = 'accessory',
  FEE = 'fee',
  TAX = 'tax',
}

export type TekionTaxEntity = {
  type: TekionEntityType.TAX;
  desc: string;
  taxCode: string;
};

export type TekionFeeEntity = {
  type: TekionEntityType.FEE;
  desc: string;
  name: string;
};

export type TekionFNIEntity = {
  type: TekionEntityType.FNI;
  desc: string;
  providerId: string;
  productId: string;
};

export type TekionAccessoryEntity = {
  type: TekionEntityType.ACCESSORY;
  desc: string;
  accessoryId: string;
  accessoryCode: string;
};


export type TekionEntity = TekionTaxEntity | TekionFeeEntity | TekionFNIEntity | TekionAccessoryEntity;

export enum UAREntityType {
  ACCESSORY = 'accessory',
  ADD_ON = 'addon',
  PRODUCT = 'product',
  FEE = 'fee',
  TAX = 'tax',
}
